const Direction = {
  RIGHT: "right",
  DOWN: "down",
};

const GRID_X_SIZE = 14;
const GRID_Y_SIZE = 14;
const EMPTY = "-";
const MAX_SOLUTIONS = 15000; // Maximum number of solutions to explore

/**
 * Utility to deep clone a grid.
 */
function cloneGrid(grid) {
  return grid.map((row) => [...row]);
}

/**
 * Checks if a word can be placed at (x, y) with the given direction,
 * not breaking crossword rules.
 */
class CrosswordGenerator {
  constructor() {
    /**
     * Array of { solution: PlacedWord[], score: number }
     */
    this.topSolutionsWithScores = [];
  }

  /**
   * Place a word on a new grid, returns the new grid
   */
  placeWord(x, y, word, direction, grid) {
    const new_grid = cloneGrid(grid);
    if (direction === Direction.RIGHT) {
      for (let i = 0; i < word.length; i++) new_grid[y][x + i] = word[i];
    } else {
      for (let i = 0; i < word.length; i++) new_grid[y + i][x] = word[i];
    }
    return new_grid;
  }

  /**
   * Check if (x, y, word, direction) is in bounds and
   * doesn't overwrite any letters except at intersections.
   */
  basicPlacementCheck(x, y, word, direction, grid) {
    if (direction === Direction.RIGHT) {
      if (x < 0 || x + word.length > GRID_X_SIZE) return false;
      for (let i = 0; i < word.length; i++) {
        const cell = grid[y][x + i];
        if (cell !== EMPTY && cell !== word[i]) return false;
      }
    } else {
      if (y < 0 || y + word.length > GRID_Y_SIZE) return false;
      for (let i = 0; i < word.length; i++) {
        const cell = grid[y + i][x];
        if (cell !== EMPTY && cell !== word[i]) return false;
      }
    }
    return true;
  }

  // Returns true if any cells of word1 and word2 are adjacent without intersection
  adjacentNoIntersect(x1, y1, len1, dir1, x2, y2, len2, dir2) {
    // For all cells in word1
    for (let i = 0; i < len1; ++i) {
      let cx = dir1 === Direction.RIGHT ? x1 + i : x1;
      let cy = dir1 === Direction.RIGHT ? y1 : y1 + i;
      // For all cells in word2
      for (let j = 0; j < len2; ++j) {
        let dx = dir2 === Direction.RIGHT ? x2 + j : x2;
        let dy = dir2 === Direction.RIGHT ? y2 : y2 + j;
        // If same cell => intersection allowed
        if (cx === dx && cy === dy) continue;
        // If adjacent orthogonally
        if (Math.abs(cx - dx) <= 1 && Math.abs(cy - dy) <= 1) {
          return true; // Not allowed
        }
      }
    }
    return false;
  }

  // Helper functions for interference checks for clarity
  rightRightInterference(x, y, wordLen, px, py, pwordLen) {
    // Same row within one cell vertically, and x-ranges overlap with margin of 1
    return (
      Math.abs(y - py) <= 1 &&
      ((x - 1 <= px && px < x + wordLen + 1) ||
        (px - 1 <= x && x < px + pwordLen + 1))
    );
  }
  downDownInterference(x, y, wordLen, px, py, pwordLen) {
    // Same col within one cell horizontally, and y-ranges overlap with margin of 1
    return (
      Math.abs(x - px) <= 1 &&
      ((y - 1 <= py && py < y + wordLen + 1) ||
        (py - 1 <= y && y < py + pwordLen + 1))
    );
  }

  rightDownInterference(x, y, lenDown, px, py, lenRight) {
  // Placed word: RIGHT at (px, py)
  // New word: DOWN at (x, y)
  for(let i=0; i<lenDown; ++i) {
    let cx = x;
    let cy = y + i;
    if(
      cy === py &&
      cx >= px && cx < px + lenRight
    ) {
      return false; // Their cells overlap: intersection allowed!
    }
  }
  // If they don't intersect, check adjacency
  if(this.adjacentNoIntersect(
      x, y, lenDown, Direction.DOWN,
      px, py, lenRight, Direction.RIGHT
    )) return true; // interference!
  return false;
}

  downRightInterference(x, y, lenRight, px, py, lenDown) {
    // Placed word: DOWN at (px, py)
    // New word: RIGHT at (x, y)
    for (let i = 0; i < lenRight; ++i) {
      let cx = x + i;
      let cy = y;
      if (
        cx === px &&
        cy >= py && cy < py + lenDown
      ) {
        return false; // Their cells overlap: intersection allowed!
      }
    }
    // If they don't intersect, check adjacency
    if (this.adjacentNoIntersect(
        x, y, lenRight, Direction.RIGHT,
        px, py, lenDown, Direction.DOWN
        )) return true; // interference!
    return false;
  }

  /**
   * Checks for interference with already placed words.
   */
  interferenceCheck(x, y, word, direction, solution) {
    for (let placedWord of solution) {

      const { x: px, y: py, answer: pword, direction: pdirection } = placedWord;

      if (
        pdirection === Direction.RIGHT &&
        direction === Direction.RIGHT &&
        this.rightRightInterference(x, y, word.length, px, py, pword.length)
      )
        return false;

      if (
        pdirection === Direction.DOWN &&
        direction === Direction.DOWN &&
        this.downDownInterference(x, y, word.length, px, py, pword.length)
      )
        return false;

      if (
        pdirection === Direction.RIGHT &&
        direction === Direction.DOWN &&
        this.rightDownInterference(x, y, word.length, px, py, pword.length)
      )
        return false;

      if (
        pdirection === Direction.DOWN &&
        direction === Direction.RIGHT &&
        this.downRightInterference(x, y, word.length, px, py, pword.length)
      )
        return false;
    }
    return true;
  }

  /**
   * Master check for whether a word can be placed.
   */
  canPlaceWord(x, y, word, direction, solution, grid) {
    // Check boundaries and collisions first.
    if (!this.basicPlacementCheck(x, y, word, direction, grid)){
        return false;
    }
    // Check interference rules.
    if (!this.interferenceCheck(x, y, word, direction, solution)){
        return false;
    }
    return true;
  }

  /**
   * Score: maximize # of words and intersections by minimizing total letters used.
   */
  calculateScore(solution, grid) {
    let nWords = solution.length;
    let nLetters = grid.flat().filter((cell) => cell !== EMPTY).length;
    return nWords * 100 - nLetters;
  }

  /**
   * Try to add one new word to all current solutions.
   */
  addWord(wordWithClue) {
    let word = wordWithClue.answer;
    let clue = wordWithClue.clue;
    let newSolutionsWithScores = [];
    for (let i = 0; i < this.topSolutionsWithScores.length; i++) {
      let solutionWithScore = this.topSolutionsWithScores[i];
      let solution = solutionWithScore.solution;

      // Build the grid for this solution so far.
      let grid = Array.from(Array(GRID_Y_SIZE), () =>
        Array(GRID_X_SIZE).fill(EMPTY)
      );
      for (let j = 0; j < solution.length; j++) {
        let placedWord = solution[j];
        grid = this.placeWord(
          placedWord.x,
          placedWord.y,
          placedWord.answer,
          placedWord.direction,
          grid
        );
      }

      // Try all positions/directions for this word.
      for (let x = 0; x < GRID_X_SIZE; x++) {
        for (let y = 0; y < GRID_Y_SIZE; y++) {
          for (let direction of [Direction.RIGHT, Direction.DOWN]) {
            // Stop if we have too many solutions
            if (newSolutionsWithScores.length > MAX_SOLUTIONS) break;

            if (this.canPlaceWord(x, y, word, direction, solution, grid)) {
              let new_grid = this.placeWord(x, y, word, direction, grid);
              let newSolution = solution.concat({
                x: x,
                y: y,
                direction: direction,
                answer: word,
                clue: clue,
              });
              let newScore = this.calculateScore(newSolution, new_grid);
              newSolutionsWithScores.push({
                solution: newSolution,
                score: newScore,
              });
            }
          }
        }
      }
    }

    // If no solution is found for this word.
    if (newSolutionsWithScores.length === 0) {
      console.log("No solution found for word: ", word);
      return;
    }

    // Keep only top scoring solutions.
    newSolutionsWithScores.sort((a, b) => b.score - a.score);
    this.topSolutionsWithScores = newSolutionsWithScores.slice(0, 15000);
  }

  /**
   * Generates crossword solutions for the given words+clues array.
   */
  generate(wordsWithClue) {
    // Input is of type:
    // [
    //   { answer: "word1", clue: "clue1" },
    //   { answer: "word2", clue: "clue2" },
    //   ...
    // ]
    // Display input words
    // console.log("Input words with clues:");
    // for (let wordWithClue of wordsWithClue) {
    //     console.log(`Word: ${wordWithClue.answer}, Clue: ${wordWithClue.clue}`);
    // }
    
    // Start with an empty solution
    this.topSolutionsWithScores = [{ solution: [], score: 0 }];

    // Add each word to the crossword
    for (let wordWithClue of wordsWithClue) {
      this.addWord(wordWithClue);
    }

    if (this.topSolutionsWithScores.length === 0) {
      console.log("No solutions found");
      return {
        gridSolutions: null,
        gridXLength: 1,
        gridYLength: 1,
      };
    }

    // Deep copy so we don't mutate original placedWord objects when rescaling
    const topSolutionCopy = this.topSolutionsWithScores[0].solution.map(
      (w) => ({ ...w })
    );

    // Display the top solution
    // console.log("Top solution found:");
    // for (let placedWord of topSolutionCopy) {
    //     console.log(
    //         `Word: ${placedWord.answer}, Position: (${placedWord.x}, ${placedWord.y}), Direction: ${placedWord.direction}, Clue: ${placedWord.clue}`
    //         );
    // }

    // Calculate true boundaries of the grid
    let minX = GRID_X_SIZE;
    let minY = GRID_Y_SIZE;
    let maxX = 0;
    let maxY = 0;

    for (let placedWord of topSolutionCopy) {
      let { x, y, direction, answer } = placedWord;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (direction === Direction.RIGHT) {
        if (x + answer.length > maxX) maxX = x + answer.length;
        if (y >= maxY) maxY = y + 1;
      } else {
        if (x >= maxX) maxX = x + 1;
        if (y + answer.length > maxY) maxY = y + answer.length;
      }
    }

    // Rescale all solutions to fit the true boundaries
    for (let placedWord of topSolutionCopy) {
      placedWord.x -= minX;
      placedWord.y -= minY;
    }

    return {
      gridSolutions: topSolutionCopy,
      gridXLength: maxX - minX,
      gridYLength: maxY - minY,
    };
  }
}

/**
 * Selects random entries from an array using a provided RNG function.
 */
function selectRandomEntries(rng, wordsArray, numberOfWords) {
  function shuffle(array) {
    let currentIndex = array.length,
      randomIndex;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(rng() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }

    return array;
  }

  const shuffledWords = shuffle([...wordsArray]);
  return shuffledWords.slice(0, numberOfWords);
}

export { CrosswordGenerator, selectRandomEntries };
